import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import useDocuments from "/b2c/query/useDocuments.b2c";
import { useAmwellInbox } from "/hook/useAmwellInbox/useAmwellInbox";

import {
  DocumentsContextState,
  DocumentsProviderProps,
  ReadStatusMap,
} from "./DocumentsProvider.types";

const DocumentsContext = createContext<DocumentsContextState | undefined>(undefined);
const STORAGE_KEY = "in_office_read_status";

/*
  data: the top level data which contains totals and other global data
  docs: helper object containing the actual document objects
  error: specific error type for In-office or Video
  loading: loading flag for each type
*/
export const DocumentsProvider: React.FC<DocumentsProviderProps> = ({ children }) => {
  const { data: inOfficeData, error: inOfficeError, loading: loadingInOfficeDocs } = useDocuments();
  const {
    data: videoData,
    error: videoError,
    loading: loadingVideoDocs,
  } = useAmwellInbox({ maxResults: 50 });

  // Extract the docs for each type
  const inOfficeDocs = useMemo(() => inOfficeData?.patient?.letters?.items || [], [inOfficeData]);
  const videoDocs = useMemo(() => videoData?.messages || [], [videoData]);

  // Local storage for In-office messages
  const [readStatusMap, setReadStatusMap] = useState<ReadStatusMap>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(readStatusMap));
  }, [readStatusMap]);

  const markAsRead = useCallback((letterId: string) => {
    setReadStatusMap((prev) => ({ ...prev, [letterId]: true }));
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(readStatusMap));
  }, [readStatusMap]);

  // Unread message counts
  const inOfficeUnreadCount = useMemo(() => {
    return inOfficeDocs.filter((doc) => !readStatusMap[doc.letterId]).length || 0;
  }, [inOfficeDocs, readStatusMap]);

  const videoUnreadCount = useMemo(() => {
    return videoData?.unread || 0;
  }, [videoData]);

  const value = useMemo(
    () => ({
      inOfficeData,
      videoData,
      inOfficeDocs,
      videoDocs,
      inOfficeError,
      videoError: !!videoError,
      loadingInOfficeDocs: loadingInOfficeDocs,
      loadingVideoDocs: loadingVideoDocs,
      totalUnreadCount: inOfficeUnreadCount + videoUnreadCount,
      readStatusMap,
      markAsRead,
      inOfficeUnreadCount,
      videoUnreadCount,
    }),
    [
      inOfficeData,
      videoData,
      inOfficeDocs,
      videoDocs,
      inOfficeError,
      videoError,
      loadingInOfficeDocs,
      loadingVideoDocs,
      readStatusMap,
      markAsRead,
      inOfficeUnreadCount,
      videoUnreadCount,
    ],
  );

  return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>;
};

export const useDocumentsContext = () => {
  const context = useContext(DocumentsContext);
  if (!context) throw new Error("useDocumentsContext must be used within DocumentsProvider");
  return context;
};
